/**
 * Chunked Upload Utility
 * Splits large files into 1MB chunks for reliable uploads
 * Supports progress tracking, retry, and resume
 */

const API = '/api';
const CHUNK_SIZE = 1024 * 1024; // 1MB - matches backend

/**
 * Upload a file using chunked upload
 * @param {File} file - The file to upload
 * @param {string} token - Auth token
 * @param {Function} onProgress - Progress callback (0-100)
 * @param {AbortSignal} signal - Optional abort signal
 * @returns {Promise<object>} - Upload result with URLs
 */
export async function chunkedUpload(file, token, onProgress = () => {}, signal = null) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  
  // For small files (< 1MB), use single upload
  if (totalChunks <= 1) {
    return singleUpload(file, token, onProgress, signal);
  }
  
  // Step 1: Initialize upload session
  const initResponse = await fetch(`${API}/upload/chunked/init`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      filename: file.name,
      filesize: file.size,
      totalChunks: totalChunks
    }),
    signal
  });
  
  if (!initResponse.ok) {
    const error = await initResponse.json().catch(() => ({}));
    throw new Error(error.detail || 'Nie udało się zainicjować uploadu');
  }
  
  const { uploadId } = await initResponse.json();
  
  // Step 2: Upload chunks
  let uploadedChunks = 0;
  const maxRetries = 3;
  
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    
    let retries = 0;
    let success = false;
    
    while (!success && retries < maxRetries) {
      try {
        const formData = new FormData();
        formData.append('file', chunk, `chunk_${i}`);
        
        const chunkResponse = await fetch(`${API}/upload/chunked/${uploadId}/${i}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
          signal
        });
        
        if (!chunkResponse.ok) {
          throw new Error(`Chunk ${i} failed`);
        }
        
        success = true;
        uploadedChunks++;
        
        // Report progress
        const progress = Math.round((uploadedChunks / totalChunks) * 90); // 0-90% for upload
        onProgress(progress, `Wysyłanie ${uploadedChunks}/${totalChunks}...`);
        
      } catch (err) {
        retries++;
        if (retries >= maxRetries) {
          throw new Error(`Nie udało się wysłać części ${i + 1}/${totalChunks} po ${maxRetries} próbach`);
        }
        // Wait before retry (exponential backoff)
        await new Promise(r => setTimeout(r, 1000 * retries));
      }
    }
  }
  
  // Step 3: Complete upload
  onProgress(95, 'Przetwarzanie...');
  
  const completeResponse = await fetch(`${API}/upload/chunked/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      uploadId: uploadId,
      filename: file.name
    }),
    signal
  });
  
  if (!completeResponse.ok) {
    const error = await completeResponse.json().catch(() => ({}));
    throw new Error(error.detail || 'Nie udało się przetworzyć pliku');
  }
  
  onProgress(100, 'Gotowe!');
  
  return completeResponse.json();
}

/**
 * Single file upload (for small files < 1MB)
 */
async function singleUpload(file, token, onProgress, signal) {
  const formData = new FormData();
  formData.append('file', file);
  
  onProgress(10, 'Wysyłanie...');
  
  const response = await fetch(`${API}/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData,
    signal
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Upload failed');
  }
  
  onProgress(100, 'Gotowe!');
  return response.json();
}

/**
 * Validate file before upload
 */
export function validateFile(file, options = {}) {
  const {
    maxSize = 12 * 1024 * 1024, // 12MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
  } = options;
  
  if (!allowedTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: 'Dozwolone formaty: JPG, PNG, WEBP' 
    };
  }
  
  if (file.size > maxSize) {
    return { 
      valid: false, 
      error: `Plik zbyt duży (${(file.size / 1024 / 1024).toFixed(1)}MB). Max ${maxSize / 1024 / 1024}MB` 
    };
  }
  
  return { valid: true };
}

export default chunkedUpload;
